<template>
    <div class="space-y-3">
        <LogicSubjectSection :page="page" :graph="graph" :node="node" :context="context" />

        <section class="space-y-1.5">
            <form-label>Conditions (all must hold)</form-label>
            <div v-for="(item, index) in draft.conditions" :key="item.id" class="flex items-center gap-1.5">
                <form-select class="w-24 flex-shrink-0 text-sm" v-model="item.property">
                    <option v-if="item.property && !properties.includes(item.property)" :value="item.property">{{ item.property }} · saved</option>
                    <option v-for="property in properties" :key="property" :value="property">{{ property }}</option>
                </form-select>
                <form-select class="w-16 flex-shrink-0 text-sm" v-model="item.operator">
                    <option v-for="op in CONDITION_OPERATORS" :key="op" :value="op">{{ op }}</option>
                </form-select>
                <form-input v-model="item.value" type="text" placeholder="value" class="flex-1 text-sm" />
                <button class="ha-builder-danger-button" title="Remove condition" @click="draft.conditions.splice(index, 1)">×</button>
            </div>
            <button class="ha-builder-link-button" @click="addCondition">+ condition</button>
        </section>

        <form-checkbox label="Only when the outcome changes" v-model="draft.onChange" />
        <p class="text-xs text-gray-400">
            Runs its actions once each time the conditions start to hold, e.g. when a sensor crosses a threshold.
        </p>

        <LogicRunList :page="page" :graph="graph" :node="node" port="then" label="Then (in order)"
            empty="Drag a wire from Then to an action, condition or switch." />
    </div>
</template>

<script setup>
import { computed, toRaw } from 'vue';
import { useLogicStore } from '../../stores/logicStore.js';
import { CONDITION_OPERATORS } from '../../Hasp/properties/v2/logic/index.js';
import { getSubjectPropertyChoices } from '../../logic/logicFlow.js';
import { useNodeDraft } from '../hass/useNodeDraft.js';
import LogicRunList from './LogicRunList.vue';
import LogicSubjectSection from './LogicSubjectSection.vue';

/** A condition node: what it compares, its conditions and what it runs when they hold. */
const props = defineProps({
    page: { type: Object, required: true },
    graph: { type: Object, required: true },
    node: { type: Object, required: true },
    context: { type: Object, required: true },
});
const logic = useLogicStore();

const properties = computed(() => getSubjectPropertyChoices(props.graph, props.node.id, props.context, props.page));

const draft = useNodeDraft({
    build: () => ({
        conditions: structuredClone(toRaw(props.node).data.conditions ?? []),
        onChange: Boolean(props.node.data.onChange),
    }),
    snapshot: value => value,
    current: () => ({ conditions: props.node.data.conditions ?? [], onChange: Boolean(props.node.data.onChange) }),
    commit: data => logic.setNodeData(props.page, props.node.id, { ...toRaw(props.node).data, ...data }),
});

function addCondition() {
    const ids = draft.value.conditions.map(item => Number(String(item.id).replace(/^c/, '')) || 0);
    draft.value.conditions.push({ id: `c${Math.max(0, ...ids) + 1}`, property: properties.value[0] ?? 'val', operator: '==', value: '' });
}
</script>
